import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PageHelper } from 'Components/PageHelper';
import { Vinculos } from './Bonds';
import { Main as Container } from 'Components/Main';
import { FormSuccess } from 'Components/Form/Success';
import { Detalhes } from './Details';
import { addAsset, editAsset, getAssetById } from 'services/assets-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { Dialog, PageHeader, Button } from 'Components/ui';
import { useQuery } from 'react-query';
import { PageLoaderForm } from 'Components/PageLoader/Form';

const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';

const TabsBar = styled.div`
  display: flex;
  gap: 4px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  width: fit-content;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 24px;
`;
const Tab = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$active ? BLUE : 'transparent'};
  color: ${p => p.$active ? 'white' : MUTED};
  font-weight: ${p => p.$active ? 600 : 500};
`;

export const AssetsForm = () => {
  const { t } = useTranslation(['asm']);
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const navigate = useNavigate();
  const [idAsset, setIdAsset] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const { register, handleSubmit, formState: { errors }, control, getValues, setValue, reset } = useForm({
    defaultValues: {
      name: '', criticity: '', type: '', dataProcessing: '', location: '',
      pii: false, user: '', description: '', tags: [], areas: [], processes: [], systems: [], companyKey: null,
    },
  });

  const handleComplete = async () => {
    try {
      setIsLoadingForm(true);
      const assetFormmated = formatForm(getValues());
      const res = id ? await editAsset(id, assetFormmated) : await addAsset(assetFormmated);
      if (res.status === 200 || res.status === 201) {
        setIdAsset(res.data._id ?? id);
        setFinished(true);
        setIsLoadingForm(false);
      } else {
        throw new Error('Ocorreu um erro ao salvar/editar um Ativo!');
      }
    } catch (error) {
      setIsLoadingForm(false);
      Dialog.alert({
        title: t('asm:assets.delete.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const formatProcess = p => ({ _id: p._id, name: p.name, description: p.description, user: p.user, criticity: p.criticity });
  const formatArea = a => ({ _id: a._id, name: a.name, description: a.description, user: a.user, criticity: a.criticity });
  const formatSystem = s => ({ _id: s._id, name: s.name, description: s.description, criticity: s.criticity, user: s.user });

  const formatForm = dados => ({
    name: dados.name,
    criticity: dados.criticity,
    type: dados.type,
    user: dados.user,
    tags: dados.tags.length > 0 ? dados.tags : [],
    pii: dados.pii,
    location: dados.location,
    description: dados.description,
    companyId: dados.user?.companyId,
    areas: dados.areas.length > 0 ? dados.areas.map(formatArea) : [],
    processes: dados.processes.length > 0 ? dados.processes.map(formatProcess) : [],
    systems: dados.systems.length > 0 ? dados.systems.map(formatSystem) : [],
    companyKey: dados.companyKey ?? selectedTenant.slug,
  });

  const handleReset = () => {
    reset();
    setActiveStep(0);
    setFinished(false);
    setIdAsset(null);
    navigate('/assets/add');
  };

  const { isLoading, error, data: asset } = useQuery(['getAsset', id], async () => {
    if (!id) return null;
    const resAsset = await getAssetById(id);
    if (!resAsset.data) return null;
    return resAsset.data;
  });

  useEffect(() => {
    if (!id || !asset) return;
    Object.keys(asset).forEach(key => setValue(key, asset[key]));
  }, [id, asset, setValue]);

  const showForm = () => id ? !isLoading && !error : true;

  return (
    <Container>
      {isLoading && <PageLoaderForm />}
      {showForm() && !finished && (
        <form onSubmit={handleSubmit(handleComplete)}>
          <PageHeader
            data-tour="page-title"
            title={id ? t('asm:assets.form.editTitle') : t('asm:assets.form.addTitle')}
            segments={[
              { label: t('common:breadcrumbs.mapping') },
              { label: t('asm:assets.title'), route: '/assets' },
              { label: id ? t('asm:assets.form.editTitle') : t('asm:assets.form.addTitle') },
            ]}
            actions={
              <>
                <Button variant="outline" type="button" onClick={() => navigate('/assets')} sx={{ height: '44px', padding: '0 20px' }}>
                  {t('common:buttons.cancel')}
                </Button>
                <Button variant="primary" type="submit" disabled={isLoadingForm} sx={{ height: '44px', fontSize: '14px', padding: '0 20px' }}>
                  {t('asm:assets.form.save')}
                </Button>
              </>
            }
          />
          <div data-tour="stepper">
            <TabsBar>
              <Tab type="button" $active={activeStep === 0} onClick={() => setActiveStep(0)}>
                {t('asm:assets.form.detailsStep')}
              </Tab>
              <Tab type="button" $active={activeStep === 1} onClick={() => setActiveStep(1)}>
                {t('asm:assets.form.bondsStep')}
              </Tab>
            </TabsBar>
          </div>
          {activeStep === 0 && (
            <Detalhes
              control={control}
              register={register}
              errors={errors}
              getValues={getValues}
              id={id}
            />
          )}
          {activeStep === 1 && (
            <Vinculos control={control} />
          )}
        </form>
      )}
      {finished && (
        <FormSuccess
          title={t('asm:assets.form.success')}
          labelStartAgain={t('asm:assets.form.addAnother')}
          handleStartAgain={handleReset}
          labelShow={t('asm:assets.form.viewDetails')}
          handleShow={() => navigate('/assets/show/' + idAsset)}
        />
      )}
      {!finished && <PageHelper steps={tourSteps} tourKey="assets-form" />}
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao formulário de cadastro de Ativos! Aqui você pode adicionar ou editar os recursos que suportam suas operações.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Use os botões no canto superior direito para cancelar ou salvar o ativo.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="stepper"]',
    content: 'O cadastro é dividido em 2 abas: Detalhes (informações básicas) e Vínculos (associações com áreas, processos e sistemas).',
    disableScrolling: true,
  },
  {
    target: '[data-tour="asset-name"]',
    content: 'Digite o nome do ativo. Este campo é obrigatório e não pode ser alterado após a criação.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-criticity"]',
    content: 'Selecione a criticidade do ativo. A escala vai de 1 (Muito Baixa) a 5 (Muito Alta).',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-type"]',
    content: 'Escolha o tipo do ativo: Pessoa, Tecnologia ou Ambiente.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-user"]',
    content: 'Selecione o responsável pelo ativo. Este campo é obrigatório.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-tags"]',
    content: 'Adicione tags para categorizar o ativo. Digite e pressione Enter para adicionar cada tag.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-location"]',
    content: 'Informe a localização física ou lógica do ativo.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-pii"]',
    content: 'Marque se o ativo processa PII — dados pessoais que podem identificar um indivíduo.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-description"]',
    content: 'Descreva o ativo em detalhes. Este campo é obrigatório.',
    disableScrolling: false,
  },
];
